const BASE = '/api'

let currentUserId: number | null = null

export function setCurrentUser(id: number | null) {
  currentUserId = id
}

export function getCurrentUserId(): number | null {
  return currentUserId
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> || {}),
  }
  // D-120: cookie httpOnly de sessão é a fonte de auth primária (via
  // `credentials: 'include'`). Header `X-Usuario-Id` é fallback transicional
  // — backend aceita ambos. Remove o header em D-121+ depois dos caches
  // de browser dos atendentes renovarem.
  if (currentUserId) {
    headers['X-Usuario-Id'] = String(currentUserId)
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',  // envia cookie de sessão (myros_session)
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || err.erro || res.statusText)
  }

  if (res.headers.get('content-type')?.includes('application/json')) {
    return res.json()
  }
  return res as unknown as T
}

// Auth
export const getPerfis = () => request<any[]>('/auth/perfis')
export const login = (usuario_id: number, senha?: string) =>
  request<any>('/auth/login', { method: 'POST', body: JSON.stringify({ usuario_id, senha }) })
// D-120: logout deleta sessão server-side + zera cookie httpOnly.
export const logout = () =>
  request<{ ok: true }>('/auth/logout', { method: 'POST' })

// Mesas
export const getMesas = () => request<any[]>('/mesas')
export const getMesa = (id: number) => request<any>(`/mesas/${id}`)
/** D-106: pega o id real da mesa Balcão (numero=0, id auto-increment). */
export const getMesaBalcao = () => request<{ id: number; numero: number; observacao: string }>('/mesas/balcao')
export const updateMesa = (id: number, data: any) =>
  request<any>(`/mesas/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
export const desativarMesa = (id: number) =>
  request<any>(`/mesas/${id}/desativar`, { method: 'PATCH' })

// Comandas
export const criarComanda = (data: any) =>
  request<any>('/comandas', { method: 'POST', body: JSON.stringify(data) })
export const getComanda = (id: number) => request<any>(`/comandas/${id}`)
export const updateComanda = (id: number, data: any) =>
  request<any>(`/comandas/${id}`, { method: 'PATCH', body: JSON.stringify(data) })
export const deleteComanda = (id: number) =>
  request<any>(`/comandas/${id}`, { method: 'DELETE' })

// Itens — API returns grouped object { categoria: item[] }, flatten to array
export const getItens = async (incluirInativos?: boolean): Promise<any[]> => {
  const url = incluirInativos ? '/itens?incluir_inativos=1' : '/itens'
  const grouped = await request<Record<string, any[]>>(url)
  return Object.values(grouped).flat()
}
export const getItem = (id: number) => request<any>(`/itens/${id}`)
export const criarItem = (data: any) =>
  request<any>('/itens', { method: 'POST', body: JSON.stringify(data) })
export const updateItem = (id: number, data: any) =>
  request<any>(`/itens/${id}`, { method: 'PUT', body: JSON.stringify(data) })

// Lancamentos
export const criarLancamento = (data: any) =>
  request<any>('/lancamentos', { method: 'POST', body: JSON.stringify(data) })
export const cancelarLancamento = (id: number) =>
  request<any>(`/lancamentos/${id}/cancelar`, { method: 'PATCH', body: JSON.stringify({ usuario_id: currentUserId }) })

// Favoritos
export const getFavoritos = (usuarioId: number) => request<any[]>(`/favoritos/${usuarioId}`)
export const addFavorito = (data: any) =>
  request<any>('/favoritos', { method: 'POST', body: JSON.stringify(data) })
export const removeFavorito = (id: number) =>
  request<any>(`/favoritos/${id}`, { method: 'DELETE' })
export const reordenarFavoritos = (data: any) =>
  request<any>('/favoritos/reordenar', { method: 'PUT', body: JSON.stringify(data) })

// Fechamento
export const calcularFechamento = (data: any) =>
  request<any>('/fechamentos/calcular', { method: 'POST', body: JSON.stringify(data) })
export const finalizarFechamento = (data: any) =>
  request<any>('/fechamentos', { method: 'POST', body: JSON.stringify(data) })
export const getFechamento = (id: number) => request<any>(`/fechamentos/${id}`)
export const getRecibo = (id: number) => request<any>(`/fechamentos/${id}/recibo`)
export const reimprimirRecibo = (id: number) =>
  request<any>(`/fechamentos/${id}/reimprimir`, { method: 'POST', body: JSON.stringify({ usuario_id: currentUserId }) })
export const logImpressao = (data: { mesa_id: number; tipo: 'impressao' | 'pdf'; contexto: string; usuario_id: number }) =>
  request<any>('/fechamentos/log-impressao', { method: 'POST', body: JSON.stringify(data) })

// Buscar cliente por nome
export const buscarCliente = (nome: string) => request<any[]>(`/comandas/buscar?nome=${encodeURIComponent(nome)}`)

// Configurações
export const getValorQuiloConfig = () => request<Record<string, string>>('/configuracoes/valor-quilo')

// Rateio
export const ratearIgualitario = (data: any) =>
  request<any>('/rateios/igualitario', { method: 'POST', body: JSON.stringify(data) })
export const ratearManual = (data: any) =>
  request<any>('/rateios/manual', { method: 'POST', body: JSON.stringify(data) })

// Saida
export const validarSaida = (codigo: string) =>
  request<any>('/saida/validar', { method: 'POST', body: JSON.stringify({ codigo }) })
export const getSaidaPendentes = (mesaId: number) =>
  request<any[]>(`/saida/pendentes/${mesaId}`)

// Reservas
export const getReservas = () => request<any[]>('/reservas')
export const criarReserva = (data: any) =>
  request<any>('/reservas', { method: 'POST', body: JSON.stringify(data) })
export const removerReserva = (id: number) =>
  request<any>(`/reservas/${id}`, { method: 'DELETE', body: JSON.stringify({ usuario_id: currentUserId }) })

// Historico
export const getHistorico = (params?: any) => {
  const qs = new URLSearchParams(params || {}).toString()
  return request<any[]>(`/historico?${qs}`)
}
export const getHistoricoPagamentos = (params?: any) => {
  const qs = new URLSearchParams(params || {}).toString()
  return request<any[]>(`/historico/pagamentos?${qs}`)
}
export const exportarXlsx = (params?: any) => {
  const qs = new URLSearchParams(params || {}).toString()
  return fetch(`${BASE}/historico/xlsx?${qs}`, {
    headers: currentUserId ? { 'X-Usuario-Id': String(currentUserId) } : {},
  })
}

// Usuarios
export const getUsuarios = () => request<any[]>('/usuarios')
export const criarUsuario = (data: any) =>
  request<any>('/usuarios', { method: 'POST', body: JSON.stringify(data) })
export const updateUsuario = (id: number, data: any) =>
  request<any>(`/usuarios/${id}`, { method: 'PUT', body: JSON.stringify(data) })
export const desativarUsuario = (id: number) =>
  request<any>(`/usuarios/${id}/desativar`, { method: 'PATCH' })

// Valores Extras (gorjetas, adicionais, arredondamentos por funcionário)
export const getValoresExtrasFuncionario = (id: number, de?: string, ate?: string) => {
  const qs = new URLSearchParams()
  if (de) qs.set('de', de)
  if (ate) qs.set('ate', ate)
  return request<any>(`/valores-extras/funcionario/${id}?${qs}`)
}
export const getValoresExtrasResumo = (de?: string, ate?: string) => {
  const qs = new URLSearchParams()
  if (de) qs.set('de', de)
  if (ate) qs.set('ate', ate)
  return request<any[]>(`/valores-extras/resumo?${qs}`)
}
export const limparValoresExtras = (de?: string, ate?: string) =>
  request<{ removidos: number; mensagem: string }>(
    `/valores-extras/limpar`,
    { method: 'DELETE', body: JSON.stringify({ de, ate }) }
  )

// Salvar ajustes pendentes na mesa (atendente salva, caixa carrega)
export const salvarAjustesMesa = (mesaId: number, ajustes: any[]) =>
  request<any>(`/mesas/${mesaId}`, { method: 'PATCH', body: JSON.stringify({ ajustes_pendentes: ajustes }) })

// Logs
export const getLogs = (limit?: number) => request<any[]>(`/logs?limit=${limit || 100}`)

// =====================================================
// Vales de Funcionários (D-89, 2026-05-04)
// =====================================================
export type ValeCategoria = "bebidas" | "compras_pessoais" | "antecipacao_salario"

export interface Vale {
  id: number
  funcionario_id: number
  funcionario_nome: string
  categoria: ValeCategoria
  valor_centavos: number
  data_lancamento: string
  mes_referencia: string
  criado_por_admin_id: number
  criado_por_admin_nome: string
  observacao: string | null
  created_at: string
}

export interface ValesListResponse {
  vales: Vale[]
  mes_referencia: string
  funcionario_id: number | null
  soma_mes_centavos: number
  limite_mensal_centavos: number
}

export const listarVales = (params?: { funcionario_id?: number; mes_referencia?: string }) => {
  const qs = new URLSearchParams()
  if (params?.funcionario_id) qs.set("funcionario_id", String(params.funcionario_id))
  if (params?.mes_referencia) qs.set("mes_referencia", params.mes_referencia)
  return request<ValesListResponse>(`/vales?${qs}`)
}

export const criarVale = (data: {
  funcionario_id: number
  categoria: ValeCategoria
  valor_centavos: number
  mes_referencia?: string
  observacao?: string
}) => request<Vale>("/vales", { method: "POST", body: JSON.stringify(data) })

export const editarVale = (
  id: number,
  data: { categoria?: ValeCategoria; valor_centavos?: number; observacao?: string },
) => request<Vale>(`/vales/${id}`, { method: "PATCH", body: JSON.stringify(data) })

export const deletarVale = (id: number) =>
  request<void>(`/vales/${id}`, { method: "DELETE" })

export const meusVales = (mes_referencia?: string) => {
  const qs = new URLSearchParams()
  if (mes_referencia) qs.set("mes_referencia", mes_referencia)
  return request<ValesListResponse>(`/vales/me?${qs}`)
}

// D-95 Fase 2: SSO token para entrar no admin do site sem prompt.
export const emitirSsoToken = () =>
  request<{ token: string; ttl_seconds: number }>('/auth/sso-token', { method: 'POST' })

// D-95 Fase 3 (dashboard): KPIs agregados pro admin.
export interface DashboardKpis {
  hoje: {
    faturamento: number
    /** @deprecated alias de `mesas_unicas` mantido por compat. D-101. */
    mesas_atendidas: number
    /** Atendimentos = COUNT(*) de fechamentos pagos no dia (mesa reaberta 3× = 3). D-101. */
    atendimentos: number
    /** Mesas distintas no dia (mesa reaberta 3× = 1). D-101. */
    mesas_unicas: number
    /** D-112: COUNT de comandas fechadas em mesas com fechamento pago hoje. */
    clientes_atendidos?: number
    /** @deprecated alias de `ticket_por_mesa`. */
    ticket_medio: number
    /** Ticket médio por comanda fechada (faturamento / atendimentos). D-101. */
    ticket_por_comanda: number
    /** Ticket médio por mesa (faturamento / mesas_unicas). D-101. */
    ticket_por_mesa: number
    gorjetas: number
  }
  faturamento_7_dias: Array<{ data: string; valor: number }>
  gorjetas_atendentes_mes: Array<{ usuario_id: number; nome: string; total: number }>
  top_itens_hoje: Array<{ descricao: string; qtd: number; valor_total: number }>
  status_mesas: { livre: number; ocupada: number; pagamento_parcial: number; reservada: number }
}
// D-162 R7 (2026-05-26): date opcional pra consultar histórico (?date=YYYY-MM-DD).
export const getDashboard = (date?: string) =>
  request<DashboardKpis>(`/admin/dashboard${date ? `?date=${encodeURIComponent(date)}` : ''}`)

// ─── Caixa / Turno (D-102, 2026-05-16) ─────────────────────────────────
export interface TurnoCaixa {
  id: number
  usuario_id: number
  valor_inicial: number
  abertura_em: string
  status: 'aberto' | 'fechado'
  total_vendido_preview_centavos?: number
  /** D-138: Z-Number sequencial pra fechamento Z fiscal (PDV-like) */
  z_numero_sequencial?: number | null
}

export interface ReciboTurno {
  id: number
  operador: string
  valor_inicial_centavos: number
  abertura_em: string
  fechamento_em: string | null
  total_vendido_centavos: number
  /** D-112: venda em dinheiro físico digitada pelo operador. */
  venda_dinheiro_centavos?: number
  /** D-112: total_vendido - venda_dinheiro (PIX/cartão). */
  outras_formas_centavos?: number
  /** D-119: soma sangrias do turno. */
  sangrias_centavos?: number
  /** D-119: soma suprimentos do turno. */
  suprimentos_centavos?: number
  valor_final_centavos: number
  esperado_centavos: number
  diferenca_centavos: number
  observacao: string | null
  fechado_por_nome: string | null
  status?: string
  /** D-138: Z-Number sequencial */
  z_numero_sequencial?: number | null
  /** D-138: motivo obrigatório quando diferença ≠ 0 */
  diferenca_motivo?: string | null
}

export interface CaixaMovimentacao {
  id: number
  tipo: 'sangria' | 'suprimento'
  valor_centavos: number
  motivo: string | null
  criada_em: string
  criada_por_nome: string | null
}

export interface MovimentacoesResponse {
  movimentacoes: CaixaMovimentacao[]
  totais: { sangrias: number; suprimentos: number; saldo_liquido: number }
}

export const getTurnoCaixaAtual = () =>
  request<{ aberto: boolean; turno: TurnoCaixa | null }>('/caixa/atual')

export const abrirCaixa = (valor_inicial_centavos: number) =>
  request<{ ok: true; turno: TurnoCaixa; z_numero_sequencial?: number | null }>('/caixa/abrir', {
    method: 'POST',
    body: JSON.stringify({ valor_inicial_centavos }),
  })

// D-138: assinatura ampliada com `diferenca_motivo`. Mantém compat com 3-arg
// chamadas legacy. `venda_dinheiro_centavos` continua opcional (default 0 no
// fluxo PDV novo — dinheiro deixa de ser forma de pagamento).
export const fecharCaixa = (
  valor_final_centavos: number,
  observacao?: string,
  venda_dinheiro_centavos: number = 0,
  diferenca_motivo?: string,
) =>
  request<{ ok: true; recibo: ReciboTurno }>('/caixa/fechar', {
    method: 'POST',
    body: JSON.stringify({
      valor_final_centavos,
      venda_dinheiro_centavos,
      observacao,
      diferenca_motivo,
    }),
  })

export const getReciboTurno = (id: number) =>
  request<{ recibo: ReciboTurno }>(`/caixa/turno/${id}`)

// D-119: Sangria + Suprimento
export const registrarMovimentacao = (params: {
  tipo: 'sangria' | 'suprimento'
  valor_centavos: number
  motivo?: string
}) => request<{ ok: true; movimentacao: CaixaMovimentacao }>('/caixa/movimentacao', {
  method: 'POST',
  body: JSON.stringify(params),
})

export const getMovimentacoes = (turno_id?: number) => {
  const qs = turno_id ? `?turno_id=${turno_id}` : ''
  return request<MovimentacoesResponse>(`/caixa/movimentacoes${qs}`)
}

// ─── Trocar senha (D-110, 2026-05-17) ──────────────────────────────────
// Próprio usuário: precisa senha_atual + nova_senha.
// Admin pode trocar de qualquer um via usuario_id_alvo.
export const trocarSenha = (params: {
  senha_atual?: string
  nova_senha: string
  usuario_id_alvo?: number
}) => request<{ ok: true }>('/auth/trocar-senha', {
  method: 'POST',
  body: JSON.stringify(params),
})

